import { memo, useCallback, useMemo, lazy, Suspense } from 'react';
import type { StationFormData } from '../stationForm';
import { NumberField } from './NumberField';

// bundle-dynamic-imports: Lazy load heavy Leaflet map component
const StationMap = lazy(() => import('./StationMap'));

// Map loading skeleton
const MapSkeleton = () => (
  <div
    style={{
      height: '100%',
      width: '100%',
      background: '#f1f5f9',
      borderRadius: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#94a3b8'
    }}
  >
    <span className="material-symbols-outlined" style={{ fontSize: '2rem', marginRight: '0.5rem' }}>map</span>
    Loading map...
  </div>
);

interface LocationSectionProps {
  formData: StationFormData;
  onFieldChange: <K extends keyof StationFormData>(
    field: K,
    value: StationFormData[K]
  ) => void;
}

export const LocationSection = memo(function LocationSection({
  formData,
  onFieldChange,
}: LocationSectionProps) {
  const handleAddressChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFieldChange('address', e.target.value);
    },
    [onFieldChange]
  );

  const handlePositionChange = useCallback(
    (lat: number, lng: number) => {
      onFieldChange('latitude', lat);
      onFieldChange('longitude', lng);
    },
    [onFieldChange]
  );

  const handleLatitudeChange = useCallback(
    (value: number) => onFieldChange('latitude', value),
    [onFieldChange]
  );

  const handleLongitudeChange = useCallback(
    (value: number) => onFieldChange('longitude', value),
    [onFieldChange]
  );

  const markerPosition: [number, number] = useMemo(
    () => [formData.latitude, formData.longitude],
    [formData.latitude, formData.longitude]
  );

  return (
    <section className="form-section">
      <h2 className="section-title">
        <span className="material-symbols-outlined section-icon">map</span>
        Station Location
      </h2>

      <div className="location-content">
        {/* Address Input */}
        <div className="form-field">
          <label className="field-label">Station Address *</label>
          <div className="search-field">
            <span className="material-symbols-outlined search-icon">location_on</span>
            <input
              type="text"
              className="search-input"
              placeholder="Enter station address..."
              value={formData.address}
              onChange={handleAddressChange}
              required
            />
          </div>
          <span className="field-hint">Full address where the station is located</span>
        </div>

        {/* Leaflet Map - Lazy Loaded */}
        <div className="map-container">
          <Suspense fallback={<MapSkeleton />}>
            <StationMap
              position={markerPosition}
              onPositionChange={handlePositionChange}
            />
          </Suspense>
        </div>

        {/* Drag hint */}
        <p className="map-hint">
          <span className="material-symbols-outlined">info</span>
          Click on the map or drag the marker to set exact GPS coordinates
        </p>

        {/* Editable Coordinates */}
        <div className="coordinates-edit">
          <span className="coordinates-label">COORDINATES:</span>
          <div className="coordinates-inputs">
            <div className="coordinate-field">
              <label className="coordinate-input-label">Latitude</label>
              <NumberField
                className="coordinate-input"
                value={formData.latitude}
                onChange={handleLatitudeChange}
                step="0.0001"
                min={-90}
                max={90}
                placeholder="50.4501"
              />
            </div>
            <div className="coordinate-field">
              <label className="coordinate-input-label">Longitude</label>
              <NumberField
                className="coordinate-input"
                value={formData.longitude}
                onChange={handleLongitudeChange}
                step="0.0001"
                min={-180}
                max={180}
                placeholder="30.5234"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

export default LocationSection;
